/*
 * DFU abstract transport. Port of pc-nrf-dfu-js DfuAbstractTransport.js.
 * Implements the payload-chunking + resume-on-CRC-match logic that is common
 * across serial and BLE transports. Subclasses must implement the 5 wire
 * primitives: createObject, writeObject, crcObject, executeObject, selectObject.
 */

import { crc32 } from "../util/crc32.js";
import { DfuError, ErrorCode } from "./errors.js";

export type ObjectType = 0x01 | 0x02; // 1=init packet ("command"), 2=firmware ("data")

export interface SelectResult {
  offset: number;
  crc: number;
  chunkSize: number;
}

export interface CrcResult {
  offset: number;
  crc: number;
}

export type ProgressPhase = "init" | "firmware";

export interface ProgressEvent {
  phase: ProgressPhase;
  bytesSent: number;
  bytesTotal: number;
}

export type ProgressCallback = (e: ProgressEvent) => void;

export abstract class DfuAbstractTransport {
  protected onProgress: ProgressCallback | undefined;

  setProgressCallback(cb: ProgressCallback | undefined): void {
    this.onProgress = cb;
  }

  // Subclass primitives — mirror the 5 DFU requests.
  abstract createObject(type: ObjectType, size: number): Promise<void>;
  abstract writeObject(
    bytes: Uint8Array,
    crcSoFar: number | undefined,
    offsetSoFar: number,
  ): Promise<unknown>;
  abstract crcObject(): Promise<CrcResult>;
  abstract executeObject(): Promise<void>;
  abstract selectObject(type: ObjectType): Promise<SelectResult>;
  abstract abortObject(): Promise<void>;

  restart(): Promise<void> {
    return this.createObject(0x01, 0x10);
  }

  abort(): Promise<void> {
    return this.abortObject();
  }

  sendInitPacket(bytes: Uint8Array): Promise<void> {
    return this.sendPayload(0x01, bytes);
  }

  sendFirmwareImage(bytes: Uint8Array): Promise<void> {
    return this.sendPayload(0x02, bytes);
  }

  private payloadPhase(type: ObjectType): ProgressPhase {
    return type === 0x01 ? "init" : "firmware";
  }

  private async sendPayload(
    type: ObjectType,
    bytes: Uint8Array,
    resumeAtChunkBoundary = false,
  ): Promise<void> {
    const sel = await this.selectObject(type);
    const { offset, crc: crcSoFar, chunkSize } = sel;

    this.onProgress?.({ phase: this.payloadPhase(type), bytesSent: 0, bytesTotal: bytes.length });

    if (offset !== 0) {
      const crc = crc32(bytes.subarray(0, offset));
      if (crc === crcSoFar) {
        if (offset === bytes.length) {
          // Already transferred. Send execute just in case the previous attempt died right before exec.
          await this.executeObject();
          this.onProgress?.({
            phase: this.payloadPhase(type),
            bytesSent: bytes.length,
            bytesTotal: bytes.length,
          });
          return;
        }
        if (offset % chunkSize === 0 && !resumeAtChunkBoundary) {
          // Edge case: page-boundary, can't tell if exec was acked. Resend exec; it's idempotent.
          await this.executeObject();
          await this.sendPayload(type, bytes, true);
          return;
        }
        // Partial chunk: continue from offset
        const end = Math.min(bytes.length, offset + chunkSize - (offset % chunkSize));
        await this.sendAndExecutePayloadChunk(type, bytes, offset, end, chunkSize, crc);
        return;
      }
      throw new DfuError(ErrorCode.ERROR_PRE_DFU_INTERRUPTED);
    }

    const end = Math.min(bytes.length, chunkSize);
    await this.createObject(type, end);
    await this.sendAndExecutePayloadChunk(type, bytes, 0, end, chunkSize);
  }

  private async sendAndExecutePayloadChunk(
    type: ObjectType,
    bytes: Uint8Array,
    start: number,
    end: number,
    chunkSize: number,
    crcSoFar?: number,
  ): Promise<void> {
    await this.sendPayloadChunk(type, bytes, start, end, chunkSize, crcSoFar);
    await this.executeObject();

    this.onProgress?.({
      phase: this.payloadPhase(type),
      bytesSent: end,
      bytesTotal: bytes.length,
    });

    if (end >= bytes.length) return;

    const nextEnd = Math.min(bytes.length, end + chunkSize);
    await this.createObject(type, nextEnd - end);
    await this.sendAndExecutePayloadChunk(type, bytes, end, nextEnd, chunkSize, crc32(bytes.subarray(0, end)));
  }

  private async sendPayloadChunk(
    type: ObjectType,
    bytes: Uint8Array,
    start: number,
    end: number,
    chunkSize: number,
    crcSoFar?: number,
    retries = 0,
  ): Promise<void> {
    const subarray = bytes.subarray(start, end);
    const crcAtChunkEnd = crc32(subarray, crcSoFar);

    try {
      await this.writeObject(subarray, crcSoFar, start);
      const { offset, crc } = await this.crcObject();
      if (offset !== end) {
        throw new DfuError(
          ErrorCode.ERROR_UNEXPECTED_BYTES,
          `Expected ${end} bytes sent, target reports ${offset}.`,
        );
      }
      if (crcAtChunkEnd !== crc) {
        throw new DfuError(
          ErrorCode.ERROR_CRC_MISMATCH,
          `CRC mismatch at ${end} bytes: expected 0x${crcAtChunkEnd.toString(16)}, got 0x${crc.toString(16)}.`,
        );
      }
    } catch (err) {
      if (retries >= 5) {
        const msg = err instanceof Error ? err.message : String(err);
        throw new DfuError(ErrorCode.ERROR_TOO_MANY_WRITE_FAILURES, `Last failure: ${msg}`);
      }
      const newStart = start - (start % chunkSize);
      const rewoundCrc = newStart === 0 ? undefined : crc32(bytes.subarray(0, newStart));
      await this.createObject(type, end - start);
      await this.sendPayloadChunk(type, bytes, newStart, end, chunkSize, rewoundCrc, retries + 1);
    }
  }
}
